// E8TrapezoidGardenSeedsOpenTask.js
// Zadanie otwarte E8 (2026 zad. 19) - pole trapezu, liczba opakowa? i koszt.

import React from "react";
import { BlockMath, InlineMath } from "../../../components/math/KatexWrappers";
import SvgMathLabel from "../../../components/diagrams/SvgMathLabel";
import FractionInput from "../../../components/inputs/FractionInput";
import { scoreStaged } from "../../openTaskScoring";

function rawStr(v) {
  if (v == null) return "";
  if (typeof v === "string") return v;
  if (typeof v.raw === "string") return v.raw;
  if (typeof v.value === "string") return v.value;
  return "";
}

const CASES = [
  { baseA: 18, baseB: 12, height: 9, coverage: 25, price: 23.8 },
  { baseA: 20, baseB: 14, height: 8, coverage: 20, price: 18.5 },
  { baseA: 16, baseB: 10, height: 10, coverage: 25, price: 19.9 },
  { baseA: 24, baseB: 18, height: 7, coverage: 30, price: 21.5 },
];

function pick(arr) {
  return arr[Math.floor(Math.random() * arr.length)];
}

function buildData(values) {
  const area = ((values.baseA + values.baseB) * values.height) / 2;
  const packages = Math.ceil(area / values.coverage);
  const cost = Math.round(packages * values.price * 100) / 100;
  return { ...values, area, packages, cost, maxPoints: 3 };
}

function createOriginalTask() {
  return { type: "E8TrapezoidGardenSeedsOpenTask", data: buildData(CASES[0]), source: { variant: "original", exam: "E8", session: "2026_MAJ", taskNumber: 19 }, tags: ["e8", "planimetria", "trapez", "pole", "zadanie-tekstowe", "zadanie-otwarte"] };
}

export function generateTask(generationOptions = {}) {
  if (generationOptions.variant === "original") return createOriginalTask();
  return { type: "E8TrapezoidGardenSeedsOpenTask", data: buildData(pick(CASES.slice(1))), source: { variant: "random", basedOn: "E8_2026_MAJ_19" }, tags: ["e8", "planimetria", "trapez", "pole", "zadanie-tekstowe", "zadanie-otwarte"] };
}

function normalize(text) { return rawStr(text).replace(/,/g, ".").replace(/\s+/g, ""); }
function parseNumber(text) { const m = normalize(text).match(/\d+(?:\.\d+)?/); return m ? Number(m[0]) : null; }
function money(value) { return value.toFixed(2).replace(".", "{,}"); }

function Diagram({ data }) {
  const fill = "rgba(255,159,10,0.18)", accent = "#ff9f0a";
  return (
    <div style={{ display: "flex", justifyContent: "center", margin: "14px 0" }}>
      <svg width="340" height="190" viewBox="0 0 340 190" role="img" aria-label="Ogródek w kształcie trapezu">
        <polygon points="70,140 270,140 230,55 110,55" fill={fill} stroke="currentColor" strokeWidth="2.5" />
        <line x1="110" y1="55" x2="110" y2="140" stroke={accent} strokeWidth="2.5" strokeDasharray="5 4" />
        <SvgMathLabel x={170} y={38} math={`${data.baseB}\\ \\text{ m}`} width={60} height={22} fontSize={15} />
        <SvgMathLabel x={170} y={166} math={`${data.baseA}\\ \\text{ m}`} width={60} height={22} fontSize={15} />
        <SvgMathLabel x={118} y={102} math={`${data.height}\\ \\text{ m}`} width={60} height={22} fontSize={15} color={accent} align="left" />
      </svg>
    </div>
  );
}

export function renderTask({ data }) {
  return <div style={{ textAlign: "left", lineHeight: 1.7 }}><p>Ogródek pani Anny ma kształt trapezu, którego podstawy mają długości <InlineMath math={`${data.baseA}\\ \\ \\text{m}`} /> i <InlineMath math={`${data.baseB}\\ \\ \\text{m}`} />, a wysokość jest równa <InlineMath math={`${data.height}\\ \\ \\text{m}`} />. Pani Anna chce zmienić swój ogródek w łąkę kwietną. Jedno opakowanie nasion wystarcza na obsianie <InlineMath math={`${data.coverage}\\ \\ \\text{m}^2`} /> powierzchni i kosztuje <InlineMath math={`${money(data.price)}\\ \\ \\text{zł}`} />.</p><p>Oblicz, ile złotych musi zapłacić pani Anna za najmniejszą liczbę opakowań z nasionami potrzebnych na obsianie całej powierzchni tego ogródka. Zapisz obliczenia.</p></div>;
}

export function renderInput({ data }, userAnswer = {}, onChange, disabled) {
  const ua = userAnswer || {};
  const set = (field) => (val) => onChange?.({ ...ua, [field]: val });
  return (
    <div style={{ display: "grid", gap: 14, textAlign: "left" }}>
      <div style={{ fontSize: 12.5, opacity: 0.78, lineHeight: 1.45, borderLeft: "3px solid var(--color-accent)", paddingLeft: 8 }}>
        <strong>{"Wyniki po\u015brednie s\u0105 opcjonalne."}</strong>{" "}
        {"Wpisz je, je\u015bli chcesz dosta\u0107 punkty cz\u0105stkowe. Poprawny koszt z jednostk\u0105 daje pe\u0142ne 3 punkty."}
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>{"Pole ogr\u00f3dka:"}</div>
        <FractionInput
          value={ua.area || ""}
          onChange={set("area")}
          disabled={disabled}
          label=""
          suffixLatex="\\ \\text{m}^2"
          placeholder={`np. ${data.area}`}
        />
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>{"Najmniejsza liczba opakowa\u0144:"}</div>
        <FractionInput
          value={ua.packages || ""}
          onChange={set("packages")}
          disabled={disabled}
          label=""
          suffixLatex="\\ \\text{opak.}"
          placeholder={`np. ${data.packages}`}
        />
      </div>
      <div>
        <div style={{ marginBottom: 4 }}>Koszt:</div>
        <FractionInput
          value={ua.cost || ""}
          onChange={set("cost")}
          disabled={disabled}
          label=""
          suffixLatex="\\ \\text{z\u0142}"
          placeholder={`np. ${money(data.cost).replace("{,}", ",")}`}
        />
      </div>
    </div>
  );
}
export function checkAnswer({ data }, userAnswer = {}) {
  const area = parseNumber(userAnswer.area);
  const packages = parseNumber(userAnswer.packages);
  const cost = parseNumber(userAnswer.cost);
  const resultOk = cost != null && Math.abs(cost - data.cost) < 0.01;
  const areaOk = area != null && Math.abs(area - data.area) < 0.01;
  const packagesOk = packages != null && Math.abs(packages - data.packages) < 0.01;
  return scoreStaged({
    resultOk,
    milestones: [areaOk, packagesOk],
    maxScore: data.maxPoints,
    parts: { "pole trapezu": areaOk, "liczba opakowa\u0144": packagesOk, "koszt": resultOk },
  });
}
export function renderAnswer({ data }) {
  const packagesRatio = String(data.area / data.coverage).replace(".", "{,}");
  return (
    <div style={{ textAlign: "left", lineHeight: 1.7 }}>
      <div>{"Przyk\u0142adowa poprawna odpowied\u017a:"}</div>
      <BlockMath math={`P=\\frac{(${data.baseA}+${data.baseB})\\cdot ${data.height}}{2}=${data.area}\\ \\ \\text{m}^2`} />
      <BlockMath math={`\\frac{${data.area}}{${data.coverage}}=${packagesRatio}`} />
      <BlockMath math={`${data.packages}\\cdot ${money(data.price)}=${money(data.cost)}\\ \\ \\text{z\u0142}`} />
      <div>{"Odpowied\u017a: pani Anna zap\u0142aci "}<InlineMath math={`${money(data.cost)}\\ \\ \\text{z\u0142}`} />{"."}</div>
    </div>
  );
}

export function renderUserAnswer(userAnswer = {}) {
  const area = rawStr(userAnswer.area);
  const packages = rawStr(userAnswer.packages);
  const cost = rawStr(userAnswer.cost);
  if (!area && !packages && !cost) return <i>(brak odpowiedzi)</i>;
  return (
    <div style={{ textAlign: "left", display: "grid", gap: 4 }}>
      <div>{"Pole ogrodu: "}<InlineMath math={`${area || "--"}\\ \\text{m}^2`} /></div>
      <div>{"Najmniejsza liczba opakowa\u0144: "}<InlineMath math={`${packages || "--"}\\ \\text{opak.}`} /></div>
      <div>{"Koszt: "}<InlineMath math={`${cost || "--"}\\ \\text{z\u0142}`} /></div>
    </div>
  );
}
export function renderExplanation({ data }) { return <div style={{ textAlign: "left", lineHeight: 1.75 }}><Diagram data={data} /><p><strong>Krok 1.</strong> Obliczamy pole trapezu.</p><BlockMath math={`P=\\frac{(${data.baseA}+${data.baseB})\\cdot ${data.height}}{2}=${data.area}\\ \\ \\text{m}^2`} /><p><strong>Krok 2.</strong> Obliczamy najmniejszą liczbę opakowań.</p><BlockMath math={`\\frac{${data.area}}{${data.coverage}}=${String(data.area / data.coverage).replace(".", "{,}")}`} /><p>Trzeba kupić <InlineMath math={`${data.packages}`} /> opakowań.</p><p><strong>Krok 3.</strong> Obliczamy koszt.</p><BlockMath math={`${data.packages}\\cdot ${money(data.price)}=${money(data.cost)}\\ \\ \\text{zł}`} /><p>Pani Anna zapłaci <InlineMath math={`${money(data.cost)}\\ \\ \\text{zł}`} />.</p></div>; }
